import React, { useEffect, useRef, useState } from 'react';
import { chatbotService } from '../services/apiService.jsx';
import './ChatbotWidget.css';

const WELCOME_MESSAGE = "Hi! I'm the BTS assistant. Ask me anything - I don't have access to your company's live data, but I can help with general questions.";

const ChatbotWidget = () => {
  const [open, setOpen] = useState(false);
  const [checkedStatus, setCheckedStatus] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [messages, setMessages] = useState([{ role: 'assistant', content: WELCOME_MESSAGE }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!open || checkedStatus) return;
    chatbotService.getStatus()
      .then((res) => setConfigured(Boolean(res?.data?.configured)))
      .catch(() => setConfigured(true)) // don't block the UI on a status-check failure; the send call will surface the real error
      .finally(() => setCheckedStatus(true));
  }, [open, checkedStatus]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, sending, open]);

  const handleSend = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;

    setError('');
    const history = messages.filter((m) => m.role === 'user' || m.role === 'assistant');
    const nextMessages = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setInput('');
    setSending(true);

    try {
      const res = await chatbotService.sendMessage(text, history);
      const reply = res?.data?.reply || "Sorry, I didn't get a response. Please try again.";
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="chatbot-widget">
      {open && (
        <div className="chatbot-panel" role="dialog" aria-label="AI assistant">
          <div className="chatbot-header">
            <span><i className="bi bi-stars"></i> BTS Assistant</span>
            <button type="button" className="chatbot-close" onClick={() => setOpen(false)} aria-label="Close chat">
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          {checkedStatus && !configured ? (
            <div className="chatbot-unconfigured">
              <i className="bi bi-exclamation-triangle"></i>
              <p>The AI assistant isn't set up yet. Ask an admin to add an OpenAI API key in the backend configuration.</p>
            </div>
          ) : (
            <>
              <div className="chatbot-body" ref={bodyRef}>
                {messages.map((m, idx) => (
                  <div key={idx} className={`chatbot-bubble chatbot-bubble-${m.role}`}>
                    {m.content}
                  </div>
                ))}
                {sending && (
                  <div className="chatbot-bubble chatbot-bubble-assistant chatbot-typing">
                    <span></span><span></span><span></span>
                  </div>
                )}
                {error && <div className="chatbot-error">{error}</div>}
              </div>

              <form className="chatbot-input-row" onSubmit={handleSend}>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type a message..."
                  disabled={sending}
                  maxLength={4000}
                />
                <button type="submit" disabled={sending || !input.trim()} aria-label="Send">
                  <i className="bi bi-send"></i>
                </button>
              </form>
            </>
          )}
        </div>
      )}

      <button
        type="button"
        className="chatbot-fab"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close AI assistant' : 'Open AI assistant'}
      >
        <i className={open ? 'bi bi-x-lg' : 'bi bi-chat-dots-fill'}></i>
      </button>
    </div>
  );
};

export default ChatbotWidget;
